import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import { Ruler, ScanLine, Wand2, Landmark, Zap, Droplets, Wind, ArrowRight, LayoutGrid } from "lucide-react";

const RENDER = "https://images.unsplash.com/photo-1706164971302-e30c0640cc3b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const login = () => {
  const redirectUrl = window.location.origin + "/studio";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const FEATURES = [
  { icon: LayoutGrid, c: "#38BDF8", t: "2D CAD Blueprint Editor", d: "Draw walls & rooms on a snap grid with live dimensions and square-footage." },
  { icon: Wand2, c: "#A855F7", t: "AI House Generator", d: "Describe your dream home — get a full floor plan, specs and a photoreal render." },
  { icon: ScanLine, c: "#34D399", t: "Photo → Blueprint", d: "Upload a house photo and let AI reconstruct a vector floor plan." },
  { icon: Landmark, c: "#F59E0B", t: "City Ordinance Checks", d: "Validate setbacks, FAR, lot coverage & egress against preset or custom city codes." },
];

const LAYERS = [
  { icon: Zap, c: "#38BDF8", t: "Electrical", d: "Outlets, switches, panels & lighting" },
  { icon: Droplets, c: "#34D399", t: "Plumbing", d: "Supply, drains & fixtures" },
  { icon: Wind, c: "#F59E0B", t: "HVAC", d: "Ductwork, registers & condensers" },
];

export default function Landing() {
  const { user } = useAuth();
  return (
    <div className="min-h-screen bg-[#0D1117] text-[#F0F6FC]">
      <header className="sticky top-0 z-40 glass border-b border-[#30363D]">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2" data-testid="nav-brand-logo">
            <Ruler className="w-6 h-6 text-[#38BDF8]" />
            <span className="font-heading text-xl font-bold tracking-tight">ArchiMesh<span className="text-[#38BDF8]">AI</span></span>
          </div>
          {user ? (
            <Link to="/studio"><Button className="bg-[#38BDF8] hover:bg-[#0EA5E9] text-[#0D1117] font-semibold" data-testid="nav-open-studio-btn">Open Studio <ArrowRight className="w-4 h-4 ml-1" /></Button></Link>
          ) : (
            <Button onClick={login} className="bg-[#38BDF8] hover:bg-[#0EA5E9] text-[#0D1117] font-semibold" data-testid="nav-google-login-btn">Sign in with Google</Button>
          )}
        </div>
      </header>

      <section className="max-w-7xl mx-auto px-6 pt-16 pb-24 grid lg:grid-cols-2 gap-12 items-center">
        <div className="animate-fade-up">
          <div className="inline-flex items-center gap-2 text-xs font-mono-cad uppercase tracking-widest text-[#38BDF8] border border-[#30363D] rounded-full px-3 py-1 mb-6">
            <span className="w-2 h-2 rounded-full bg-[#38BDF8] pulse-ring" /> AI Architectural Studio
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
            Design blueprints & houses with <span className="text-[#38BDF8]">precision AI</span>.
          </h1>
          <p className="mt-6 text-[#8B949E] text-base leading-relaxed max-w-lg">
            Draw floor plans on a CAD grid, generate homes from a sentence, turn a photo into a blueprint, layer in electrical / plumbing / HVAC, and check them against real city ordinances.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button onClick={user ? undefined : login} asChild={!!user} className="bg-[#38BDF8] hover:bg-[#0EA5E9] text-[#0D1117] font-semibold h-12 px-6 text-base" data-testid="hero-cta-btn">
              {user ? <Link to="/studio">Open the Studio <ArrowRight className="w-4 h-4 ml-1" /></Link> : <span>Start Designing Free</span>}
            </Button>
          </div>
          <div className="mt-8 flex gap-6 text-sm text-[#8B949E]">
            {LAYERS.map((l) => (
              <div key={l.t} className="flex items-center gap-2"><l.icon className="w-4 h-4" style={{ color: l.c }} /> {l.t}</div>
            ))}
          </div>
        </div>
        <div className="relative animate-fade-up" style={{ animationDelay: "0.15s" }}>
          <div className="absolute -inset-4 bg-[#38BDF8]/10 blur-3xl rounded-full" />
          <img src={RENDER} alt="Modern architecture" className="relative rounded-2xl border border-[#30363D] shadow-2xl object-cover w-full h-[420px]" />
          <div className="absolute bottom-4 left-4 glass rounded-lg px-4 py-2 border border-[#30363D] font-mono-cad text-xs text-[#8B949E]">
            <span className="text-[#38BDF8]">◈</span> 2,480 ft² · 4 bed · compliant
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 pb-24">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {FEATURES.map((f, i) => (
            <div key={f.t} className="rounded-2xl border border-[#30363D] bg-[#161B22] p-6 hover:border-[#484F58] transition-colors animate-fade-up" style={{ animationDelay: `${i * 0.08}s` }}>
              <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4" style={{ background: `${f.c}1a` }}>
                <f.icon className="w-5 h-5" style={{ color: f.c }} />
              </div>
              <h3 className="font-heading font-semibold text-lg">{f.t}</h3>
              <p className="text-sm text-[#8B949E] mt-2 leading-relaxed">{f.d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 pb-28">
        <div className="rounded-3xl border border-[#30363D] bg-gradient-to-b from-[#161B22] to-[#0D1117] p-10 text-center">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold">Multi-discipline blueprint layers</h2>
          <p className="text-[#8B949E] mt-3 max-w-xl mx-auto">Toggle structural, electrical, plumbing and HVAC overlays independently — just like professional CAD.</p>
          <div className="grid sm:grid-cols-3 gap-5 mt-8 max-w-3xl mx-auto">
            {LAYERS.map((l) => (
              <div key={l.t} className="rounded-xl border border-[#30363D] bg-[#0D1117] p-5">
                <l.icon className="w-6 h-6 mx-auto" style={{ color: l.c }} />
                <div className="font-heading font-semibold mt-3" style={{ color: l.c }}>{l.t}</div>
                <div className="text-xs text-[#8B949E] mt-1">{l.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <footer className="border-t border-[#30363D] py-8 text-center text-sm text-[#6E7681]">
        ArchiMesh AI — Architectural CAD & AI Blueprint Studio
      </footer>
    </div>
  );
}
